import './Books.css';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchJournals, searchBooks } from '../../services/bookService';
import Pagination from '../Pagination/Pagination';

const BOOKS_PER_PAGE = 12;

function Books() {
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(0);
    const [journals, setJournals] = useState([]);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const navigate = useNavigate();

    // Debounce search term
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
            setCurrentPage(0); // Reset to first page on new search
        }, 500);

        return () => clearTimeout(timer);
    }, [searchTerm]);

    // Fetch books when page or search term changes
    useEffect(() => {
        const loadJournals = async () => {
            try {
                setLoading(true);
                setError(null);

                let pageData;
                if (debouncedSearchTerm.trim()) {
                    // Use search endpoint if there's a search term
                    // Search by both title and authorName with the same term
                    const filters = {
                        title: debouncedSearchTerm.trim(),
                        authorName: debouncedSearchTerm.trim()
                    };
                    pageData = await searchBooks(filters, currentPage, BOOKS_PER_PAGE);
                } else {
                    // Use regular fetch if no search term
                    pageData = await fetchJournals(currentPage, BOOKS_PER_PAGE);
                }

                setJournals(pageData.content);
                setTotalPages(pageData.page.totalPages);
                setTotalElements(pageData.page.totalElements);
            } catch (err) {
                setError('Nepodařilo se načíst knihy. Zkuste to prosím znovu.');
                console.error('Error loading books:', err);
            } finally {
                setLoading(false);
            }
        };

        loadJournals();
    }, [currentPage, debouncedSearchTerm]);

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
    };

    const handleBookClick = (bookId) => {
        navigate(`/knihy/${bookId}`);
    };

    const handlePageChange = (newPage) => {
        if (newPage >= 0 && newPage < totalPages) {
            setCurrentPage(newPage);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    return (
        <div className="books-page">
            <div className="books-container">
                <h1 className="page-title">Knihy</h1>

                <div className="search-bar-container">
                    <input
                        type="text"
                        className="search-input"
                        placeholder="Vyhledat podle názvu nebo autora..."
                        value={searchTerm}
                        onChange={handleSearchChange}
                    />
                    <svg
                        className="search-icon"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                    >
                        <circle cx="11" cy="11" r="8"></circle>
                        <path d="m21 21-4.35-4.35"></path>
                    </svg>
                </div>

                {loading ? (
                    <div className="loading-container">
                        <p>Načítání knih...</p>
                    </div>
                ) : error ? (
                    <div className="error-container">
                        <p>{error}</p>
                    </div>
                ) : (
                    <>
                        <div className="books-grid">
                            {journals.length > 0 ? (
                                journals.map(journal => (
                                    <div
                                        key={journal.id}
                                        className="book-tile"
                                        onClick={() => handleBookClick(journal.id)}
                                    >
                                        <h2 className="book-title">{journal.bookTitle}</h2>
                                        <h3 className="book-author">{journal.author.name}</h3>
                                        <p className="book-description">{journal.shortSummary || journal.summarizedText}</p>
                                    </div>
                                ))
                            ) : (
                                <div className="no-results">
                                    <p>Nebyly nalezeny žádné knihy odpovídající vašemu hledání.</p>
                                </div>
                            )}
                        </div>

                        <Pagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            totalElements={totalElements}
                            onPageChange={handlePageChange}
                            itemLabel="knih"
                        />
                    </>
                )}
            </div>
        </div>
    );
}

export default Books;
